/* The rectangle arithmetic behind the crop overlay, kept out of the component
 * so it can be tested without a browser.
 *
 * All of it is in DISPLAY pixels -- what the pointer speaks. The conversion to
 * source pixels happens once, on apply; doing it per pointermove accumulates
 * rounding until the region drifts away from the rectangle on screen.
 *
 * The rules that are easy to get wrong, and that the tests pin:
 *
 *   Moving never resizes. Push a rectangle into a corner and it stops; it does
 *   not squash. That means clamping the ORIGIN to bounds minus the size, not
 *   clamping each edge separately.
 *
 *   Resizing moves two edges and leaves the opposite two alone. Dragging the
 *   north-west handle past the south-east corner must stop at the minimum
 *   size, not invert the rectangle into negative width.
 */

export function clamp(v, lo, hi) {
    return Math.min(Math.max(v, lo), Math.max(lo, hi))
}

/* Slide the whole rectangle, keeping its size. */
export function moveRect(from, dx, dy, bounds) {
    return {
        x: clamp(from.x + dx, 0, bounds.w - from.w),
        y: clamp(from.y + dy, 0, bounds.h - from.h),
        w: from.w,
        h: from.h,
    }
}

/* Drag one corner. `mode` is one of nw, ne, sw, se. */
export function resizeRect(mode, from, dx, dy, bounds, min) {
    let left = from.x
    let top = from.y
    let right = from.x + from.w
    let bottom = from.y + from.h

    if (mode[1] === "w") left = clamp(from.x + dx, 0, right - min)
    if (mode[1] === "e") right = clamp(from.x + from.w + dx, left + min, bounds.w)
    if (mode[0] === "n") top = clamp(from.y + dy, 0, bottom - min)
    if (mode[0] === "s") bottom = clamp(from.y + from.h + dy, top + min, bounds.h)

    return {x: left, y: top, w: right - left, h: bottom - top}
}

/* Eight tenths, centred: big enough to read as draggable, small enough that
 * every handle is clearly inside the picture rather than on its edge. */
export function defaultRect(bounds) {
    return {
        x: Math.round(bounds.w * 0.1),
        y: Math.round(bounds.h * 0.1),
        w: Math.round(bounds.w * 0.8),
        h: Math.round(bounds.h * 0.8),
    }
}

/* Display rectangle -> source pixels. Returns null when the result would be
 * below the minimum the transform will accept anyway, so the button can be
 * disabled rather than the crop silently discarded later. */
export function toSource(rect, shown, natural, minSourcePx) {
    if (!rect || !natural.w || !shown.w) return null
    const k = natural.w / shown.w
    const w = Math.round(rect.w * k)
    const h = Math.round(rect.h * k)
    if (w < minSourcePx || h < minSourcePx) return null
    return {x: Math.round(rect.x * k), y: Math.round(rect.y * k), w, h}
}

/* Source pixels -> display rectangle, for reopening on an existing crop. */
export function fromSource(crop, shown, natural) {
    const s = shown.w / natural.w
    return {
        x: Math.round(crop.x * s),
        y: Math.round(crop.y * s),
        w: Math.round(crop.w * s),
        h: Math.round(crop.h * s),
    }
}

/* One crop rectangle, restated across a mirror.
 *
 * The same problem `turnedRegion()` solves for a quarter turn, and it was
 * missed here for the opposite reason: rotation is deliberately NOT previewed
 * under the rectangle, so somebody had to think about the frames; a flip IS
 * previewed, with a CSS transform, so what is on screen looks right and the
 * numbers behind it quietly are not.
 *
 * While cropping, CropSurface shows the ORIGINAL file and mirrors it in CSS,
 * because the rectangle has to sit on the picture the way the visitor sees it.
 * The rectangle therefore comes back in the MIRRORED frame, while everything
 * downstream -- transformImage(), which cuts from the original and mirrors
 * afterwards, and turnedRegion(), which restates for the CDN -- reads it in
 * the original's own frame. Without this the region is reflected about the
 * picture's centre line: crop the left of what you are looking at and the
 * right of the photograph comes back.
 *
 * An INVOLUTION, which is why one function serves both directions: applying it
 * to its own output gives the rectangle back. So it is used on the way out
 * (display frame -> stored) and again on the way in (stored -> display frame),
 * and the two can never disagree about the arithmetic.
 */
export function mirroredRegion(region, natural, flipH, flipV) {
    if (!region || !natural.w || !natural.h || (!flipH && !flipV)) return region
    return {
        ...region,
        x: flipH ? natural.w - region.x - region.w : region.x,
        y: flipV ? natural.h - region.y - region.h : region.y,
    }
}
